import React from 'react'
import {AlertCircle, DollarSign, Leaf} from 'lucide-react'
import { ValidationErrors, CountryConfig, MaterialOption } from './types'
import { countryConfigs } from './constants'
import { useTranslation } from '@/i18n/useTranslation'

interface FormInputsProps {
  country: string
  setCountry: (country: string) => void
  zipCode: string
  handleZipCodeChange: (value: string) => void
  squareFootage: string
  handleSquareFootageChange: (value: string) => void
  material: string
  setMaterial: (material: string) => void
  removeOldRoof: 'yes' | 'no'
  setRemoveOldRoof: (value: 'yes' | 'no') => void
  numLayers: number
  setNumLayers: (value: number) => void
  numStories: number
  setNumStories: (value: number) => void
  roofPitch: 'low' | 'medium' | 'steep'
  setRoofPitch: (value: 'low' | 'medium' | 'steep') => void
  roofCondition: 'good' | 'fair' | 'poor'
  setRoofCondition: (value: 'good' | 'fair' | 'poor') => void
  hasChimneys: number
  setHasChimneys: (value: number) => void
  hasSkylights: number
  setHasSkylights: (value: number) => void
  hasValleys: 'few' | 'moderate' | 'many'
  setHasValleys: (value: 'few' | 'moderate' | 'many') => void
  urgency: 'flexible' | 'soon' | 'urgent'
  setUrgency: (value: 'flexible' | 'soon' | 'urgent') => void
  errors: ValidationErrors
  touched: Record<string, boolean>
  handleBlur: (field: string) => void
  currentCountry: CountryConfig
  materialOptions: MaterialOption[]
}

export default function FormInputs(props: FormInputsProps) {
  const {
    country, setCountry, zipCode, handleZipCodeChange, squareFootage, handleSquareFootageChange,
    material, setMaterial, removeOldRoof, setRemoveOldRoof, numLayers, setNumLayers,
    numStories, setNumStories, roofPitch, setRoofPitch, roofCondition, setRoofCondition,
    hasChimneys, setHasChimneys, hasSkylights, setHasSkylights, hasValleys, setHasValleys,
    urgency, setUrgency, errors, touched, handleBlur, currentCountry, materialOptions
  } = props

  const { t } = useTranslation()
  const f = t.form

  return (
    <>
      {/* Sticky Location Header */}
      <div className="sticky top-0 z-50 bg-gradient-to-r from-blue-50 to-orange-50 border-b-2 border-blue-200 shadow-md mb-6 -mx-6 sm:-mx-8 px-6 sm:px-8 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
          {/* Country Selection */}
          <div>
            <label htmlFor="country" className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              📍 {f.country}
            </label>
            <select
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-base bg-white relative z-10 font-bold text-gray-900 shadow-sm"
              aria-label={f.country}
            >
              {Object.entries(countryConfigs).map(([code, config]) => (
                <option key={code} value={code}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          {/* Postal Code */}
          <div>
            <label htmlFor="zipCode" className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide">
              📮 {currentCountry.postalCodeLabel} <span className="text-red-600" aria-label="required">*</span>
            </label>
            <input
              id="zipCode"
              type="text"
              inputMode="numeric"
              value={zipCode}
              onChange={(e) => handleZipCodeChange(e.target.value)}
              onBlur={() => handleBlur('zipCode')}
              placeholder={currentCountry.postalCodePlaceholder}
              className={`w-full px-4 py-2.5 border-2 rounded-lg focus:outline-none text-base transition-colors font-bold text-gray-900 shadow-sm ${
                errors.zipCode && touched.zipCode
                  ? 'border-red-500 focus:border-red-600 bg-red-50'
                  : 'border-gray-300 focus:border-blue-800 bg-white'
              }`}
              maxLength={10}
              aria-required="true"
              aria-invalid={!!(errors.zipCode && touched.zipCode)}
              aria-describedby={errors.zipCode && touched.zipCode ? 'zipCode-error' : undefined}
            />
            {errors.zipCode && touched.zipCode && (
              <div id="zipCode-error" className="flex items-center mt-1.5 text-red-600 text-xs" role="alert">
                <AlertCircle size={14} className="mr-1" aria-hidden="true" />
                <span>{errors.zipCode}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Info Column */}
        <div className="space-y-4">

          {/* Square Footage */}
          <div>
            <label htmlFor="squareFootage" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.squareFootage} <span className="text-red-600" aria-label="required">*</span>
            </label>
            <input
              id="squareFootage"
              type="number"
              inputMode="numeric"
              value={squareFootage}
              onChange={(e) => handleSquareFootageChange(e.target.value)}
              onBlur={() => handleBlur('squareFootage')}
              placeholder="e.g., 2000"
              className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none text-lg transition-colors ${
                errors.squareFootage && touched.squareFootage
                  ? 'border-red-500 focus:border-red-600 bg-red-50'
                  : 'border-gray-300 focus:border-blue-800'
              }`}
              min="100"
              max="100000"
              aria-required="true"
              aria-invalid={!!(errors.squareFootage && touched.squareFootage)}
              aria-describedby={errors.squareFootage && touched.squareFootage ? 'squareFootage-error squareFootage-help' : 'squareFootage-help'}
            />
            <p id="squareFootage-help" className="text-xs text-gray-500 mt-1">{f.squareFootageHelp}</p>
            {errors.squareFootage && touched.squareFootage && (
              <div id="squareFootage-error" className="flex items-center mt-2 text-red-600 text-sm" role="alert">
                <AlertCircle size={16} className="mr-1" aria-hidden="true" />
                <span>{errors.squareFootage}</span>
              </div>
            )}
          </div>

          {/* Material Selection */}
          <div>
            <label htmlFor="material" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.material}
            </label>
            <select
              id="material"
              value={material}
              onChange={(e) => setMaterial(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
              aria-label={f.material}
            >
              {materialOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Solar Tiles ROI Tooltip */}
            {material === 'solar_tiles' && (
              <div className="mt-3 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <DollarSign size={18} className="text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div className="text-sm">
                    <p className="font-bold text-green-900 mb-1">💰 {f.solarTooltipTitle}</p>
                    <p className="text-green-800 leading-relaxed">
                      <strong>{f.solarFederalCredit}</strong><br />
                      <strong>{f.solarEnergySavings}</strong><br />
                      <strong>{f.solarPayback}</strong><br />
                      <strong>{f.solarPropertyValue}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Green Roof ROI Tooltip */}
            {material === 'green_roof' && (
              <div className="mt-3 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
                <div className="flex items-start gap-2">
                  <Leaf size={18} className="text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div className="text-sm">
                    <p className="font-bold text-green-900 mb-1">🌱 {f.greenTooltipTitle}</p>
                    <p className="text-green-800 leading-relaxed">
                      <strong>{f.greenTaxCredits}</strong><br />
                      <strong>{f.greenEnergySavings}</strong><br />
                      <strong>{f.greenStormwater}</strong><br />
                      <strong>{f.greenLifespan}</strong><br />
                      <strong>{f.greenPropertyValue}</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Remove Old Roof */}
          <fieldset>
            <legend className="block text-sm font-semibold text-gray-700 mb-2">
              {f.removeOldRoof} <span className="text-red-600" aria-label="required">*</span>
            </legend>
            <div className="flex gap-4" role="group" aria-label={f.removeOldRoof}>
              <button
                type="button"
                onClick={() => setRemoveOldRoof('yes')}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  removeOldRoof === 'yes'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-pressed={removeOldRoof === 'yes'}
              >
                {f.yes}
              </button>
              <button
                type="button"
                onClick={() => setRemoveOldRoof('no')}
                className={`flex-1 py-3 px-4 rounded-lg font-semibold transition-all ${
                  removeOldRoof === 'no'
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-pressed={removeOldRoof === 'no'}
              >
                {f.no}
              </button>
            </div>
          </fieldset>

          {/* Existing Layers */}
          {removeOldRoof === 'yes' && (
            <div>
              <label htmlFor="numLayers" className="block text-sm font-semibold text-gray-700 mb-2">
                {f.existingLayers}
              </label>
              <select
                id="numLayers"
                value={numLayers}
                onChange={(e) => setNumLayers(parseInt(e.target.value))}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
                aria-label={f.existingLayers}
              >
                <option value={1}>{f.layer1}</option>
                <option value={2}>{f.layer2}</option>
                <option value={3}>{f.layer3}</option>
              </select>
            </div>
          )}

          {/* Number of Stories */}
          <div>
            <label htmlFor="numStories" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.numStories}
            </label>
            <select
              id="numStories"
              value={numStories}
              onChange={(e) => setNumStories(parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
              aria-label={f.numStories}
            >
              <option value={1}>{f.story1}</option>
              <option value={2}>{f.story2}</option>
              <option value={3}>{f.story3}</option>
            </select>
          </div>
        </div>

        {/* Advanced Options Column */}
        <div className="space-y-4">

          {/* Roof Pitch */}
          <div>
            <label htmlFor="roofPitch" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.roofPitch}
            </label>
            <select
              id="roofPitch"
              value={roofPitch}
              onChange={(e) => setRoofPitch(e.target.value as 'low' | 'medium' | 'steep')}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
              aria-label={f.roofPitch}
              aria-describedby="roofPitch-help"
            >
              <option value="low">{f.pitchLow}</option>
              <option value="medium">{f.pitchMedium}</option>
              <option value="steep">{f.pitchSteep}</option>
            </select>
            <p id="roofPitch-help" className="text-xs text-gray-500 mt-1">{f.pitchHelp}</p>
          </div>

          {/* Roof Condition */}
          <div>
            <label htmlFor="roofCondition" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.roofCondition}
            </label>
            <select
              id="roofCondition"
              value={roofCondition}
              onChange={(e) => setRoofCondition(e.target.value as 'good' | 'fair' | 'poor')}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
              aria-label={f.roofCondition}
            >
              <option value="good">{f.conditionGood}</option>
              <option value="fair">{f.conditionFair}</option>
              <option value="poor">{f.conditionPoor}</option>
            </select>
          </div>

          {/* Chimneys */}
          <div>
            <label htmlFor="hasChimneys" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.chimneys}
            </label>
            <input
              id="hasChimneys"
              type="number"
              inputMode="numeric"
              value={hasChimneys}
              onChange={(e) => setHasChimneys(Math.max(0, Math.min(5, parseInt(e.target.value) || 0)))}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg"
              min="0"
              max="5"
              aria-label={f.chimneys}
              aria-describedby="chimneys-help"
            />
            <p id="chimneys-help" className="text-xs text-gray-500 mt-1">
              {f.chimneysHelp.replace('{symbol}', currentCountry.currencySymbol)}
            </p>
          </div>

          {/* Skylights */}
          <div>
            <label htmlFor="hasSkylights" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.skylights}
            </label>
            <input
              id="hasSkylights"
              type="number"
              inputMode="numeric"
              value={hasSkylights}
              onChange={(e) => setHasSkylights(Math.max(0, Math.min(10, parseInt(e.target.value) || 0)))}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg"
              min="0"
              max="10"
              aria-label={f.skylights}
              aria-describedby="skylights-help"
            />
            <p id="skylights-help" className="text-xs text-gray-500 mt-1">
              {f.skylightsHelp.replace('{symbol}', currentCountry.currencySymbol)}
            </p>
          </div>

          {/* Roof Complexity */}
          <div>
            <label htmlFor="hasValleys" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.roofComplexity}
            </label>
            <select
              id="hasValleys"
              value={hasValleys}
              onChange={(e) => setHasValleys(e.target.value as 'few' | 'moderate' | 'many')}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
              aria-label={f.roofComplexity}
              aria-describedby="valleys-help"
            >
              <option value="few">{f.valleysFew}</option>
              <option value="moderate">{f.valleysModerate}</option>
              <option value="many">{f.valleysMany}</option>
            </select>
            <p id="valleys-help" className="text-xs text-gray-500 mt-1">{f.valleysHelp}</p>
          </div>

          {/* Project Timeline */}
          <div>
            <label htmlFor="urgency" className="block text-sm font-semibold text-gray-700 mb-2">
              {f.timeline}
            </label>
            <select
              id="urgency"
              value={urgency}
              onChange={(e) => setUrgency(e.target.value as 'flexible' | 'soon' | 'urgent')}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-800 focus:outline-none text-lg bg-white relative z-10 font-semibold text-gray-900"
              aria-label={f.timeline}
              aria-describedby="urgency-help"
            >
              <option value="flexible">{f.timelineFlexible}</option>
              <option value="soon">{f.timelineSoon}</option>
              <option value="urgent">{f.timelineUrgent}</option>
            </select>
            <p id="urgency-help" className="text-xs text-gray-500 mt-1">{f.timelineHelp}</p>
          </div>
        </div>
      </div>
    </>
  )
}
